'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Button } from '../button';
import { Input } from '../input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../card';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useToast } from '../../contexts/toast-context';
const LOGIN_MODE_STORAGE_KEY = 'textile_erp_login_mode';
export function LoginForm({ onLogin, onLoginByStaffCode, loading = false, error, title = 'Sign In', description = 'Enter your credentials to access your account', developmentDefaults }) {
    const { showError } = useToast();
    // Persist mode in localStorage so it survives any remount triggered by
    // parent loading-state flips (e.g. AuthGuard swapping to a spinner during login).
    const [mode, setMode] = useState(() => {
        if (typeof window === 'undefined')
            return 'email';
        const stored = window.localStorage.getItem(LOGIN_MODE_STORAGE_KEY);
        return stored === 'staffCode' ? 'staffCode' : 'email';
    });
    const [formData, setFormData] = useState({
        email: '',
        staffCode: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const lastShownErrorRef = useRef(undefined);
    // Update form data when developmentDefaults change or component mounts
    useEffect(() => {
        if (developmentDefaults) {
            setFormData(prev => ({
                ...prev,
                email: developmentDefaults.email || '',
                password: developmentDefaults.password || '',
            }));
        }
    }, [developmentDefaults]);
    // Surface auth errors as a toast notification
    useEffect(() => {
        if (error && error !== lastShownErrorRef.current) {
            lastShownErrorRef.current = error;
            showError('Login failed', error);
        }
        if (!error) {
            lastShownErrorRef.current = undefined;
        }
    }, [error, showError]);
    const [formErrors, setFormErrors] = useState({});
    const validateForm = () => {
        const errors = {};
        if (mode === 'email') {
            if (!formData.email) {
                errors.email = 'Email is required';
            }
            else if (!/\S+@\S+\.\S+/.test(formData.email)) {
                errors.email = 'Please enter a valid email address';
            }
            if (!formData.password) {
                errors.password = 'Password is required';
            }
            else if (formData.password.length < 8) {
                errors.password = 'Password must be at least 8 characters';
            }
        }
        else {
            if (!formData.staffCode.trim()) {
                errors.staffCode = 'Staff code is required';
            }
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        try {
            if (mode === 'email') {
                await onLogin({ email: formData.email, password: formData.password });
            }
            else {
                if (!onLoginByStaffCode) {
                    throw new Error('Staff code login is not configured');
                }
                await onLoginByStaffCode({ staffCode: formData.staffCode.trim() });
            }
        }
        catch (err) {
            // Error handling is done by parent component — user stays on current mode
        }
    };
    const handleInputChange = (field) => (e) => {
        setFormData(prev => ({
            ...prev,
            [field]: e.target.value,
        }));
        // Clear field error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }
    };
    const toggleMode = () => {
        const next = mode === 'email' ? 'staffCode' : 'email';
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(LOGIN_MODE_STORAGE_KEY, next);
        }
        setMode(next);
        setFormErrors({});
    };
    return (_jsxs(Card, { className: "w-full max-w-md mx-auto", children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsx(CardTitle, { className: "text-2xl", children: title }), _jsx(CardDescription, { children: description }), developmentDefaults && mode === 'email' && (_jsxs("div", { className: "mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700", children: [_jsx("div", { className: "font-medium mb-1", children: "\uD83E\uDDEA Development Mode" }), _jsxs("div", { children: ["Email: ", developmentDefaults.email] }), _jsxs("div", { children: ["Password: ", developmentDefaults.password] })] }))] }), _jsxs(CardContent, { children: [_jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [mode === 'email' ? (_jsxs(_Fragment, { children: [_jsx("div", { children: _jsx(Input, { label: "Email", type: "email", value: formData.email, onChange: handleInputChange('email'), error: formErrors.email, placeholder: "Enter your email", required: true, disabled: loading }) }), _jsxs("div", { className: "space-y-2", children: [_jsxs("label", { className: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70", children: ["Password", _jsx("span", { className: "text-red-500 ml-1", children: "*" })] }), _jsxs("div", { className: "relative", children: [_jsx("input", { type: showPassword ? 'text' : 'password', value: formData.password, onChange: handleInputChange('password'), placeholder: "Enter your password", required: true, disabled: loading, className: cn('flex h-10 w-full rounded-md border border-gray-300 bg-white dark:bg-gray-800 px-3 pr-10 text-sm text-gray-900 dark:text-white ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 dark:placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600', formErrors.password && 'border-red-500 focus-visible:ring-red-600') }), _jsx("button", { type: "button", className: "absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200", onClick: () => setShowPassword(!showPassword), tabIndex: -1, children: showPassword ? (_jsx(EyeOff, { className: "h-5 w-5" })) : (_jsx(Eye, { className: "h-5 w-5" })) })] }), formErrors.password && (_jsx("p", { className: "text-sm text-red-600", children: formErrors.password }))] })] })) : (_jsx("div", { children: _jsx(Input, { label: "Staff Code", type: "text", value: formData.staffCode, onChange: handleInputChange('staffCode'), error: formErrors.staffCode, placeholder: "Enter your staff code", required: true, disabled: loading, autoComplete: "off", autoFocus: true }) })), _jsx(Button, { type: "submit", className: "w-full", loading: loading, disabled: loading, children: loading ? 'Signing In...' : 'Sign In' }), developmentDefaults && mode === 'email' && (_jsx(Button, { type: "button", variant: "outline", className: "w-full", disabled: loading, onClick: async () => {
                                    setFormData(prev => ({
                                        ...prev,
                                        email: developmentDefaults.email,
                                        password: developmentDefaults.password,
                                    }));
                                    setFormErrors({});
                                    try {
                                        await onLogin({
                                            email: developmentDefaults.email,
                                            password: developmentDefaults.password,
                                        });
                                    }
                                    catch (err) {
                                        // Error handling is done by parent component
                                    }
                                }, children: "\uD83D\uDE80 Quick Login (Dev)" }))] }), _jsxs("div", { className: "mt-4 flex flex-col items-center space-y-2", children: [mode === 'email' && (_jsx("button", { type: "button", className: "text-sm text-blue-600 hover:underline", disabled: loading, children: "Forgot your password?" })), _jsx("button", { type: "button", className: "text-sm text-blue-600 hover:underline", disabled: loading, onClick: toggleMode, children: mode === 'email' ? 'Staff Code Login' : 'Email Login' })] })] })] }));
}
